/**
 * Start menu — katalog dlaždic + uživatelské přizpůsobení.
 * Standardní menu = výchozí; custom = vlastní pořadí / podmnožina.
 * Perzistence: localStorage + ui.prefs (start_menu).
 */
import { mcpCall } from './mcp';
import { uiScopeAllowsStartPath } from './ui-scope';

export interface StartMenuItem {
  /** Stabilní id (shodné s katalogem). */
  id: string;
  to: string;
  /** i18n klíč pod nav.* */
  tkey: string;
  /** CZ fallback label */
  label: string;
  /** Jméno ikony z ICON_MAP (lib/module-icons), kebab-case jako ui.icon modulů. */
  icon: string;
  wide?: boolean;
}

export interface StartMenuState {
  /** standard = katalog; custom = items (podmnožina katalogu v daném pořadí) */
  mode: 'standard' | 'custom';
  items: StartMenuItem[];
}

const LS_KEY = 'acm_start_menu_v1';
const PREFS_KEY = 'start_menu';

/** Výchozí start menu (telefon + desktop). */
export const STANDARD_START_MENU: StartMenuItem[] = [
  {
    id: 'dashboard',
    to: '/core/dashboard',
    tkey: 'dashboard',
    label: 'Dashboard',
    icon: 'layout-dashboard',
    wide: true,
  },
  {
    id: 'phone-node',
    to: '/phone/node',
    tkey: 'phoneNode',
    label: 'Nod',
    icon: 'server',
    wide: true,
  },
  { id: 'codable', to: '/phone/codable', tkey: 'phoneCodable', label: 'Codable', icon: 'codable' },
  {
    id: 'orchestrator',
    to: '/phone/orchestrator',
    tkey: 'phoneOrch',
    label: 'Orchestrator',
    icon: 'workflow',
  },
  {
    id: 'mesh',
    to: '/core/mesh',
    tkey: 'infrastructure',
    label: 'Infrastruktura',
    icon: 'network',
  },
  {
    id: 'containers',
    to: '/phone/containers',
    tkey: 'containers',
    label: 'Kontejnery',
    icon: 'box',
  },
  { id: 'proxy', to: '/phone/proxy', tkey: 'proxy', label: 'Proxy', icon: 'globe' },
  { id: 'toolbox', to: '/core/toolbox', tkey: 'toolbox', label: 'Toolbox', icon: 'wrench' },
  { id: 'tasks', to: '/core/tasks', tkey: 'tasks', label: 'Tasks', icon: 'calendar-clock' },
  { id: 'wiki', to: '/phone/wiki', tkey: 'wiki', label: 'Wiki', icon: 'book-open' },
  { id: 'backup', to: '/backup', tkey: 'backup', label: 'Backup', icon: 'archive' },
  { id: 'modules', to: '/modules', tkey: 'modules', label: 'Moduly', icon: 'layers' },
  { id: 'profile', to: '/profile', tkey: 'profile', label: 'Profil', icon: 'user-circle' },
  { id: 'apps', to: '/apps', tkey: 'apps', label: 'Apps', icon: 'users' },
  { id: 'acm-link', to: '/profile#acm-link', tkey: 'acmLink', label: 'ACMLink', icon: 'puzzle' },
];

const CATALOG = new Map(STANDARD_START_MENU.map((x) => [x.id, x]));

export function getStartCatalog(): StartMenuItem[] {
  return [...STANDARD_START_MENU];
}

const START_ITEM_MODULE: Record<string, string | null> = {
  dashboard: null,
  'phone-node': 'mesh',
  codable: 'codable',
  orchestrator: 'orchestrator',
  mesh: 'mesh',
  containers: 'docker',
  proxy: 'npm',
  toolbox: 'toolbox',
  tasks: 'tasks',
  wiki: '*',
  backup: 'backup',
  modules: null,
  profile: '*',
  apps: '*',
  'acm-link': '*',
};

/** Dlaždice podle RBAC: platforma vidí vše, Codable jen svoje + profil. */
export function startItemAllowed(
  item: StartMenuItem,
  caps?: { is_admin?: boolean; all_modules?: boolean; platform?: boolean; modules?: string[] },
): boolean {
  if (!caps) return true;
  if (caps.is_admin || caps.all_modules || caps.platform) return true;
  const mapped = START_ITEM_MODULE[item.id];
  if (mapped === '*') return true;
  if (!mapped) return false;
  return (caps.modules ?? []).includes(mapped);
}

export function resolveStartMenuItem(id: string): StartMenuItem | null {
  return CATALOG.get(id) ?? null;
}

export function defaultStartMenuState(): StartMenuState {
  return { mode: 'standard', items: [...STANDARD_START_MENU] };
}

/**
 * Starší uložená menu měla ikony jako jména komponent (`LayoutDashboard`).
 * Teď platí kebab-case jména z ICON_MAP, starý tvar se převede.
 */
export function iconKey(name: string): string {
  return name.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

function sanitize(state: unknown): StartMenuState {
  if (!state || typeof state !== 'object') return defaultStartMenuState();
  const o = state as Record<string, unknown>;
  const mode = o.mode === 'custom' ? 'custom' : 'standard';
  const rawItems = Array.isArray(o.items) ? o.items : [];
  const items: StartMenuItem[] = [];
  for (const x of rawItems) {
    if (!x || typeof x !== 'object') continue;
    const id = String((x as StartMenuItem).id ?? '');
    const fromCat = CATALOG.get(id);
    if (fromCat) {
      items.push(fromCat);
      continue;
    }
    // legacy / custom path
    const to = String((x as StartMenuItem).to ?? '');
    if (!to) continue;
    items.push({
      id: id || to,
      to,
      tkey: String((x as StartMenuItem).tkey || 'modules'),
      label: String((x as StartMenuItem).label || to),
      icon: iconKey(String((x as StartMenuItem).icon || 'layers')),
      wide: !!(x as StartMenuItem).wide,
    });
  }
  if (mode === 'standard' || items.length === 0) {
    return defaultStartMenuState();
  }
  return { mode: 'custom', items };
}

export function loadStartMenu(): StartMenuState {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return defaultStartMenuState();
    return sanitize(JSON.parse(raw));
  } catch {
    return defaultStartMenuState();
  }
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;

export function saveStartMenu(state: StartMenuState): void {
  const clean = sanitize(state);
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(clean));
  } catch { /* */ }
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    mcpCall('ui.prefs_set', { key: PREFS_KEY, value: clean }).catch(() => {});
  }, 800);
}

export async function hydrateStartMenu(): Promise<StartMenuState | null> {
  try {
    const r = await mcpCall<{ value?: unknown }>('ui.prefs_get', { key: PREFS_KEY });
    if (r?.value == null) return null;
    const s = sanitize(r.value);
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(s));
    } catch { /* */ }
    return s;
  } catch {
    return null;
  }
}

/** Efektivní seznam dlaždic k vykreslení. */
export function effectiveStartItems(state: StartMenuState): StartMenuItem[] {
  const items = state.mode === 'custom' && state.items.length > 0
    ? [...state.items]
    : [...STANDARD_START_MENU];
  // Vlastní menu vzniklo dřív, než katalog měl Aplikace. Dlaždice tam patří i tak.
  const apps = CATALOG.get('apps');
  if (apps && !items.some((item) => item.id === 'apps')) {
    const afterProfile = items.findIndex((item) => item.id === 'profile');
    items.splice(afterProfile >= 0 ? afterProfile + 1 : items.length, 0, apps);
  }
  return items.filter((x) => uiScopeAllowsStartPath(x.to));
}

export function resetStartMenuToStandard(): StartMenuState {
  const s = defaultStartMenuState();
  saveStartMenu(s);
  return s;
}

export function setCustomStartItems(ids: string[]): StartMenuState {
  const items = ids.map((id) => CATALOG.get(id)).filter((x): x is StartMenuItem => !!x);
  const s: StartMenuState = items.length ? { mode: 'custom', items } : defaultStartMenuState();
  saveStartMenu(s);
  return s;
}
